// In-app review prompt — shown once to logged-in office users, then monthly
// until they submit or choose "Don't ask again". Star rating + text + how to
// be shown publicly.
import { useEffect, useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Star, Loader2, Heart } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";

const DISPLAY_OPTS = [
  { v: "first_company", label: "First name + company" },
  { v: "full", label: "Full name + company" },
  { v: "first", label: "First name only" },
  { v: "anonymous", label: "Stay anonymous" },
];

export function ReviewPromptModal() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [body, setBody] = useState("");
  const [title, setTitle] = useState("");
  const [mode, setMode] = useState("first_company");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user) return;
    if (sessionStorage.getItem("fl_review_prompt_seen")) return;
    const t = setTimeout(() => {
      api.get("/reviews/prompt").then((r) => {
        if (r.data?.should_prompt) { setOpen(true); sessionStorage.setItem("fl_review_prompt_seen", "1"); }
      }).catch(() => {});
    }, 2500);
    return () => clearTimeout(t);
  }, [user]);

  const close = () => setOpen(false);
  const dontAsk = async () => { try { await api.post("/reviews/prompt/dismiss", { forever: true }); } catch {} setOpen(false); toast.message("No worries — we won't ask again."); };
  const submit = async () => {
    if (!rating) return toast.error("Tap a star to rate us first");
    setSaving(true);
    try {
      const { data } = await api.post("/reviews", { rating, body: body.trim(), title: title.trim(), display_mode: mode });
      setOpen(false);
      toast.success(data.status === "published" ? "Thank you! Your review is now live 🎉" : "Thanks for the feedback — our team will review it.");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not submit"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) close(); }}>
      <DialogContent className="rounded-xl max-w-md" data-testid="review-prompt-modal">
        <div className="text-center">
          <div className="w-12 h-12 rounded-full bg-orange-100 grid place-items-center mx-auto"><Heart className="w-6 h-6 text-[#FF5F15]" /></div>
          <h2 className="mt-3 text-xl font-black tracking-tight">Enjoying Fieldlink?</h2>
          <p className="text-sm text-zinc-500 mt-1">A quick review helps other NZ civil teams — and takes 20 seconds.</p>
        </div>
        <div className="flex justify-center gap-1.5 my-4" data-testid="review-stars">
          {[1, 2, 3, 4, 5].map((i) => (
            <button key={i} onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(0)} onClick={() => setRating(i)} data-testid={`review-star-${i}`} className="p-0.5">
              <Star className={`w-9 h-9 transition-transform hover:scale-110 ${i <= (hover || rating) ? "fill-[#FF5F15] text-[#FF5F15]" : "text-zinc-300"}`} />
            </button>
          ))}
        </div>
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Headline (optional)" className="w-full h-9 px-3 rounded-lg border border-zinc-300 text-sm outline-none focus:border-[#FF5F15] mb-2" data-testid="review-title" />
        <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={3} placeholder="Tell us what Fieldlink does for your crew…" className="rounded-lg text-sm" data-testid="review-body" />
        <div className="mt-3">
          <label className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 block mb-1">Show me publicly as</label>
          <Select value={mode} onValueChange={setMode}>
            <SelectTrigger data-testid="review-display-mode"><SelectValue /></SelectTrigger>
            <SelectContent>{DISPLAY_OPTS.map((o) => <SelectItem key={o.v} value={o.v}>{o.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2 mt-4">
          <Button variant="outline" onClick={close} className="flex-1 rounded-lg" data-testid="review-later">Maybe later</Button>
          <Button onClick={submit} disabled={saving} className="flex-1 rounded-lg bg-[#FF5F15] hover:bg-[#E65100] text-white font-bold" data-testid="review-submit">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit review"}
          </Button>
        </div>
        <button onClick={dontAsk} className="mt-3 w-full text-center text-xs text-zinc-400 hover:text-zinc-600 underline" data-testid="review-dont-ask">Don't ask again</button>
      </DialogContent>
    </Dialog>
  );
}
